import React, { useRef, useState, useEffect } from 'react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
} from 'recharts';

interface ResultsChartProps {
  data: {
    score: Record<string, number>;
    average: Record<string, number>;
  };
}

const ResultsChart: React.FC<ResultsChartProps> = ({ data }) => {
  const chartData = Object.keys(data.score).map(key => ({
    type: key,
    score: data.score[key] !== undefined ? parseFloat(data.score[key].toFixed(1)) : 0.0,
    average: data.average[key] !== undefined ? parseFloat(data.average[key].toFixed(1)) : 0.0,
  }));

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(window.innerWidth * 1.0);
  const [chartHeight, setChartHeight] = useState<number>(window.innerHeight * 1.0);
  const [fontSize, setFontSize] = useState<number>(10);

  useEffect(() => {
    const handleResize = () => {
      if (chartContainerRef.current) {
        const currentWidth = chartContainerRef.current.clientWidth;
        const isMobile = window.innerWidth <= 768;

        const newWidth = isMobile ? currentWidth * 1.0 : currentWidth;
        const newHeight = isMobile ? window.innerHeight * 0.6 : window.innerHeight * 0.6;

        setChartWidth(newWidth);
        setChartHeight(newHeight);

        setFontSize(isMobile ? 8 : 16);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <ul style={{ listStyleType: 'none', padding: 0, margin: 0, display: 'flex', justifyContent: 'center' }}>
        {payload.map((entry: any, index: number) => {
          const color = entry.dataKey === 'score' ? '#000' : '#000';
          const circleColor = entry.dataKey === 'score' ? '#1C2B74' : '#f4c20d';

          return (
            <li key={`item-${index}`} style={{ margin: '0 20px', display: 'flex', alignItems: 'center', color: color, padding: '5px 10px', fontSize: '12px' }}>
              <div style={{
                backgroundColor: circleColor,
                width: '12px',
                height: '12px',
                marginRight: '5px',
                borderRadius: '50%',
              }} />
              <span>{entry.dataKey === 'score' ? 'あなたの挑戦力' : '平均値'}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="chart-container" ref={chartContainerRef} style={{ width: '100%', overflow: 'hidden' }}>
      <RadarChart
        cx={chartWidth / 2}
        cy={chartHeight / 2 + 80}
        outerRadius={Math.min(chartWidth, chartHeight) / 2 - 20}
        width={chartWidth}
        height={chartHeight + 100}
        data={chartData}
      >
        <PolarGrid />
        <PolarAngleAxis
          dataKey="type"
          tick={({ payload, x, y, textAnchor, fill }) => {
            const isHorizontal = payload.coordinate === 0 || payload.coordinate === -180;
            const isVertical = payload.coordinate === 90 || payload.coordinate === -90;
            const isMobile = window.innerWidth <= 768;

            const fontSize = isMobile ? 10 : 12;

            // 横位置調整
            const dx = isHorizontal && !isMobile ? (payload.coordinate === 0 ? 20 : -20) : 0;
            // 縦位置調整
            const dy = isHorizontal
              ? (payload.coordinate === 0 ? (isMobile ? -60 : -80) : (isMobile ? -60 : -100))
              : isVertical
                ? (payload.coordinate === 90 ? (isMobile ? -5 : -20) : (isMobile ? 5 : 20))
                : 0;

            // 縦書き用にハイフンを縦棒に置き換え
            const adjustedText = payload.value.replace(/ー/g, "❘");

            // 縦書きに変換
            const verticalText = adjustedText.split("").map((char: string, index: number) => (
              <tspan
                key={index}
                x={x + dx}
                y={y + dy + index * fontSize * 1.2}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {char}
              </tspan>
            ));

            return (
              <text
                x={x + dx}
                y={y + dy}
                fontSize={fontSize}
                textAnchor="middle"
                fill={fill}
              >
                {isHorizontal ? verticalText : payload.value}
              </text>
            );
          }}
          tickLine={false}
          axisLine={false}
        />

        <PolarRadiusAxis domain={[0, 5]} tickCount={6} tick={{ fontSize: fontSize }} angle={90} />
        <Radar
          name="あなたの挑戦力"
          dataKey="score"
          stroke="#1C2B74"
          fill="#1C2B74"
          strokeWidth={2}
          fillOpacity={0}
          isAnimationActive={false}
        />
        <Radar
          name="平均値"
          dataKey="average"
          stroke="#f4c20d"
          fill="#f4c20d"
          strokeWidth={2}
          fillOpacity={0}
          isAnimationActive={false}
        />
        <Legend verticalAlign="top" content={renderLegend} />
      </RadarChart>
    </div>
  );
};

export default ResultsChart;
